import { useEffect, useMemo, useRef, useState } from "react";
import { colors } from "../../data/colors";
import CloseIcon from "@mui/icons-material/Close";
import MinimizeIcon from "@mui/icons-material/Minimize";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import ChecklistIcon from "@mui/icons-material/Checklist";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { stdObject } from "../../data/types/stdObject";
import { ModalDocType } from "../../data/types/modalTypes";
import { useTranslation } from "react-i18next";
import useScreenSize from "../../utils/getDimentions";
import {
  maxMobileSize,
  salesCodes,
  shortcutCodes,
  staffNames,
} from "../../data/main";
import convertDate from "../../utils/convertDate";
import { convertAge } from "../../utils/convertAge";
import { ageToString } from "../../utils/ageToString";
import { sexesOptions } from "../../screens/MainStructure/SearchBar/data/searchItemUtils";
import PrintIcon from "@mui/icons-material/Print";
import { callServicesRecords } from "../../services/callServicesRecords";
import { useReactToPrint } from "react-to-print";
import secureLocalStorage from "react-secure-storage";
import { useMainContext } from "../../context/MainContext";
import { useHotkeys } from "react-hotkeys-hook";
import { toShortcut } from "../../utils/toShortcut";
import { docFieldsCodes, docIdCodes } from "../../data/docCodes";

export default function useDocumentModal({
  open = false,
  info,
  close = () => {},
}: {
  open: boolean;
  info: stdObject;
  close: (v: boolean | string) => void;
}) {
  const { t } = useTranslation();
  const userAccess: stdObject = useSelector(
    (state: RootState) => state.data.userAccess,
  );
  const screenSize = useScreenSize();
  const [headerInfo, setHeaderInfo] = useState<Array<stdObject>>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const modalContentRef = useRef<HTMLDivElement | null>(null);
  const modalOverlayRef = useRef<HTMLDivElement | null>(null);
  const minimizing = useRef(false);
  const [minSizeDoc, setMinSizeDoc] = useState<boolean>(false);
  const [minModalWidth, setMinModalWidth] = useState<boolean>(false);
  const [docActive, setDocActive] = useState<stdObject>({
    status: 2,
    person: false,
    staff: false,
    interv: false,
    issues: false,
    items: false,
  });
  const staffData: Array<stdObject> = useSelector(
    (state: RootState) => state.data.staff,
  );
  const staffList = useMemo(
    () => staffData.filter((e: stdObject) => e.DOCIA1 === info.type),
    [staffData, info.type],
  );
  const [tabIndex, setTabIndex] = useState(0);
  const [secondBox, setSecondBox] = useState(false);
  const [recordFields, setRecordFields] = useState<stdObject>({});
  const [sndBoxbuttons, setSndBoxbuttons] = useState<Array<stdObject>>([]);
  const [records, setRecords] = useState<Array<stdObject> | null>(null);

  const {
    setMsg,
    setMsg4,
    closeMsg,
    setModalData,
    updateDocFilesList,
    desactivateScrollDown,
    logoutAction,
    checkIfAllMsgClean,
  } = useMainContext();

  const minimize = () => {
    const content = modalContentRef.current;
    const overlay = modalOverlayRef.current;
    const target = document.getElementById("activeDocButton");
    if (minimizing.current) return;
    if (!content || !target) {
      close("min");
      return;
    }
    minimizing.current = true;

    const from = content.getBoundingClientRect();
    const to = target.getBoundingClientRect();
    const dx = to.left + to.width / 2 - (from.left + from.width / 2);
    const dy = to.top + to.height / 2 - (from.top + from.height / 2);
    const options: KeyframeAnimationOptions = {
      duration: 400,
      easing: "cubic-bezier(0.4, 0, 0.2, 1)",
      fill: "forwards",
    };

    overlay?.animate([{ opacity: 1 }, { opacity: 0 }], options);
    const animation = content.animate(
      [
        { transform: "translate(-50%, -50%) scale(1)", opacity: 1 },
        {
          transform: `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) scale(0.05)`,
          opacity: 0.2,
        },
      ],
      options,
    );
    const finish = () => {
      minimizing.current = false;
      close("min");
    };
    animation.onfinish = finish;
    animation.oncancel = finish;
  };

  const squareButtons = [
    { Icon: MinimizeIcon, onClick: minimize },
    !minModalWidth && {
      Icon: minSizeDoc ? FullscreenIcon : FullscreenExitIcon,
      onClick: () => {
        setMinSizeDoc(!minSizeDoc);
        secureLocalStorage.setItem("docMinSize", !minSizeDoc);
      },
    },
    { Icon: CloseIcon, onClick: () => close(false), color: colors.red },
  ];
  const switchTab = (index: number) => setTabIndex(index);

  const user: any = useSelector((state: RootState) => state.data.user);
  const personItemField: Array<stdObject> = useSelector(
    (state: RootState) => state.data.per,
  );

  const recordsRef = useRef<HTMLDivElement>(null);
  const reactToPrintFn = useReactToPrint({ contentRef: recordsRef } as any);

  const {
    type,
    title = "",
    data = {},
    person = {},
    staff = [],
    issues = [],
    items = [],
    files = [],
    perFiles = [],
    tabs = [],
    onChange = () => {},
    scrollDown = false,
    buttons = [],
    mainColor = colors.blue,
  } = info as ModalDocType;

  const filesButtons = [
    {
      type,
      code: `${data[`SER${type}`].value}-${data[`COD${type}`].value}`,
      label: "c.DOC",
      list: files,
    },
    data[`CLI${type}`]?.value && {
      type: salesCodes.includes(type) ? "CLI" : "PRO",
      code: data[`CLI${type}`].value,
      label: "p.customer",
      list: perFiles,
    },
    // { key: "others", label: "p.others" },
  ];

  const staffListFiltered = staffList
    .filter(
      (e: stdObject) =>
        e.ACTIA1 || (e.CODIA1 in staff && staff[e.CODIA1].value > 0),
    )
    .map((e: stdObject, index: number) => {
      return {
        ...staff[e.CODIA1],
        label: t(staffNames[index]),
        value: staff[index]
          ? staff[index].value
          : index === 0
            ? data[`AGE${type}`]
            : null,
        name: staff[index]
          ? staff[index].name
          : index === 0
            ? data[`ANO${type}`]
            : null,
      };
    });

  const totalsData = [
    !["SAL"].includes(type) ? { code: "NET" } : null,
    // { code: "DES" },
    !["SAL"].includes(type) ? { code: "IMP" } : null,
    { code: "TOT", total: true },
    !["SAL", "COT"].includes(type) ? { code: "ABO" } : null,
    !["SAL", "COT"].includes(type) ? { code: "SAL" } : null,
  ];

  const loadFields = (fields: string[] | null = null) => {
    const values = fields ?? secureLocalStorage.getItem("docRecFields");
    const results = docFieldsCodes.reduce((acc: stdObject, curr: string) => {
      acc[curr] = Array.isArray(values) && values.includes(curr) ? 0 : 1;
      return acc;
    }, {});
    setRecordFields({ ...results });
  };

  const changeRecActive = (index: number, value: string) => {
    if (Array.isArray(records)) {
      setRecords(
        records.map((rec: stdObject, i: number) =>
          i === index ? { ...rec, active: value !== "1" } : rec,
        ),
      );
    }
  };

  useEffect(() => {
    loadFields();
  }, []);

  useEffect(() => {
    if (scrollDown) {
      setTimeout(() => {
        if (!scrollRef.current) return;
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        desactivateScrollDown();
      }, 0);
    }
  }, [scrollDown]);

  useEffect(() => {
    if (open) {
      if (minModalWidth && screenSize.width > maxMobileSize) {
        setMinModalWidth(false);
      }
      if (!minModalWidth && screenSize.width <= maxMobileSize) {
        setMinModalWidth(true);
      }
    }
  }, [screenSize]);

  const getValue = (code: string, value: string | number | undefined) => {
    switch (code) {
      case "NAC":
        return convertDate({ info: value as string, time: false, t });
      case "TIM":
        let newAge = typeof value === "string" ? convertAge(value) : "";
        return Array.isArray(newAge) ? ageToString(newAge, t) : "";
      case "GEN":
        let newValue =
          typeof value === "number"
            ? sexesOptions.find((e) => e.value === value.toString())?.label
            : "";
        return typeof newValue === "string" ? t(newValue) : "";
      default:
        return value;
    }
  };

  const updateFilesDoc = (data: stdObject) => {
    const { lFiles, update, value } = data;
    if (Array.isArray(lFiles)) {
      updateDocFilesList(lFiles);
    }
    if (update) {
      onChange("ADJ", value);
    }
  };

  const personItemFiltered = personItemField
    .filter(
      (e) =>
        getValue(e.CODPER, person[e.CODPER + e.TIPPER]) &&
        e.TIPPER === "CLI" &&
        e.ACTPER === 1 &&
        e.DOCPER === 1,
    )
    .map((e) => {
      return {
        type: "text",
        label: t(`c.${e.CODPER}`),
        value: getValue(e.CODPER, person[e.CODPER + e.TIPPER]),
        name:
          e.CODPER === "NAC"
            ? getValue("TIM", person[e.CODPER + e.TIPPER])
            : "",
      };
    });

  useEffect(() => {
    if (headerInfo.length > 0) {
      reactToPrintFn();
      setHeaderInfo([]);
    }
  }, [headerInfo]);

  const writeItemNames = (e: string) => {
    switch (e) {
      case "ARTSE0":
        return t("c.COD");
      case "NOMSE0":
        return t("c.DES");
      case "CANSE0":
        return t("c.CAN");
      case "PRESE0":
        return t("p.price");
      case "TOTSE0":
        return t("c.TOT");
    }
  };

  const openFieldsModal = () => {
    const values = secureLocalStorage.getItem("docRecFields");
    setMsg("docFieldsModal");
    setModalData({
      recordsFields: (userAccess.C.C1R >= 2
        ? docFieldsCodes
        : docFieldsCodes.slice(3)
      ).map((e: string) => {
        return {
          code: e,
          label: e.endsWith("SE0")
            ? "- " + writeItemNames(e)
            : e === "PRE"
              ? t("p.price")
              : e === "SDA"
                ? t("p.issues")
                : t(`c.${e}`),
          value: Array.isArray(values) && values.includes(e) ? 0 : 1,
        };
      }),
      setRecordFields,
    });
  };

  const prepareToPrint = () => {
    setHeaderInfo([
      {
        code: "NOM",
        name: user.NOMSUC,
        style: { fontSize: 16, fontWeight: "bolder" },
      },
      {
        code: "RSO",
        name: user.RSOSUC,
        style: { fontSize: 16, fontWeight: "bolder" },
      },
      { code: "RUC", name: user.RUCSUC, style: { fontSize: 16 } },
      {
        label: t("c.CLI"),
        name: person.NOMCLI,
        style: { width: "100%", textAlign: "left" },
      },
      {
        label: t("c.COD"),
        name: person.CODCLI,
        style: { width: "100%", textAlign: "left" },
      },
      {
        label: t("p.printDate"),
        name: "printDate",
        style: { width: "100%", textAlign: "left", marginBottom: 10 },
      },
    ]);
  };

  useEffect(() => {
    setTabIndex(0);
    setSecondBox(false);
    setRecords(null);
    if (open && type) {
      const idCode = docIdCodes[info.type];
      let onlyItemsOk =
        data[`EST3${type}`]?.value === 0 && userAccess[idCode[0]][idCode] > 1;
      if (type === "SEV" && data[`EST3${type}`]?.value < 2) {
        onlyItemsOk = user.TEC === 4 || ![3, 5].includes(user.TEC);
      }

      setDocActive({
        status: data[`EST3${type}`]?.value,
        person: onlyItemsOk,
        staff: onlyItemsOk,
        interv: onlyItemsOk,
        issues:
          (type === "SEV" && [1, 5].includes(user.TEC)) ||
          data[`EST3${type}`]?.value === 0,
        items: onlyItemsOk,
      });

      setMinSizeDoc(!!secureLocalStorage.getItem("docMinSize"));
    } else {
      setHeaderInfo([]);
    }
  }, [open]);

  const openRecords = async () => {
    setRecords(null);
    if ("CLISEV" in data && data.CLISEV.value !== "") {
      setMsg4("loading");
      const resultRecords: stdObject = await callServicesRecords(
        data.CLISEV.value,
        logoutAction,
      );
      setRecords(
        resultRecords.list.map((e: stdObject) => {
          return { ...e, active: true };
        }),
      );
      if (
        userAccess.C.C1R >= 2 &&
        Array.isArray(resultRecords.fields) &&
        resultRecords.fields.length > 0
      ) {
        loadFields(resultRecords.fields);
      }
      closeMsg(4);
    }
    setSndBoxbuttons([
      userAccess.C.C1R >= 2
        ? {
            Icon: CheckBoxIcon,
            onClick: () => selectAllRec(),
            color: colors.green,
          }
        : {},
      userAccess.C.C1R >= 2
        ? {
            Icon: CheckBoxOutlineBlankIcon,
            onClick: () => selectAllRec(false),
            color: colors.darkGrey,
          }
        : {},
      userAccess.C.C1R >= 2
        ? {
            label: t("p.print"),
            Icon: PrintIcon,
            onClick: prepareToPrint,
            color: colors.orange,
          }
        : {},
      {
        label: t("p.fields"),
        Icon: ChecklistIcon,
        onClick: openFieldsModal,
      },
      {
        label: t("p.close"),
        onClick: closeSecondBox,
        color: colors.darkGrey,
      },
    ]);
    setSecondBox(true);
  };

  const selectAllRec = (active: boolean = true) => {
    setRecords((prev) => {
      if (!Array.isArray(prev)) {
        return prev;
      }

      return prev.map((e: stdObject) => ({
        ...e,
        active,
      }));
    });
  };

  const closeSecondBox = () => {
    setSecondBox(false);
  };

  const activeButtons = buttons.reduce(
    (acc: stdObject, curr: stdObject) =>
      curr && curr.code ? { ...acc, [curr.code]: curr.onClick } : acc,
    {},
  );

  useHotkeys(
    toShortcut(shortcutCodes.save),
    () =>
      "save" in activeButtons && checkIfAllMsgClean(2) && activeButtons.save(),
    { preventDefault: true },
  );

  useHotkeys(
    toShortcut(shortcutCodes.print),
    () =>
      "print" in activeButtons &&
      checkIfAllMsgClean(2) &&
      activeButtons.print(),
    { preventDefault: true },
  );

  return {
    t,
    squareButtons,
    tabIndex,
    switchTab,
    secondBox,
    openRecords,
    staffListFiltered,
    scrollRef,
    modalContentRef,
    modalOverlayRef,
    type,
    data,
    minModalWidth,
    mainColor,
    personItemFiltered,
    onChange,
    issues,
    items,
    totalsData,
    title,
    buttons,
    tabs,
    sndBoxbuttons,
    records,
    recordsRef,
    docActive,
    filesButtons,
    headerInfo,
    minSizeDoc,
    staffList,
    recordFields,
    changeRecActive,
    updateFilesDoc,
  };
}
